import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          About ACP Conference Planner
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-2">
          A Product of Resurgence Technology Group, LLC
        </p>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
          A modern conference management platform built for churches and faith-based organizations.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Mission Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Our Story</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
            ACP Conference Planner was developed by Resurgence Technology Group, LLC to help churches and faith-based
            organizations simplify conference planning, registration, speaker management, attendee engagement, and event
            administration through modern web and mobile technology.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
            What began as a tool to help believers discover and plan for Apostolic conferences has grown into a full-featured
            platform serving event organizers, speakers, and attendees across multiple organizations. From event discovery
            and registration to ticketing, check-in, and analytics, ACP Conference Planner provides end-to-end conference
            management in one unified platform.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            Whether you're planning to attend a major denominational conference, a regional gathering, or a specialized
            ministry event — or organizing one — ACP Conference Planner is here to help you make the most of your
            conference experience.
          </p>
        </div>

        {/* What We Offer */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">What We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Event Listings</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive calendar of upcoming conferences from UPCI, WPF, ALJC, AFF, and other Pentecostal organizations.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Venue Information</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Detailed venue guides with maps, directions, and nearby amenities to help you navigate conference locations.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Travel Planning</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Flight search, hotel recommendations, and transportation options to make your travel planning seamless.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Speaker Profiles</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Learn about featured speakers, their ministries, and the topics they'll be addressing at conferences.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Detailed Schedules</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Access full conference schedules with session times, locations, and descriptions to plan your days.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 text-primary mb-4">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Save & Organize</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Save your favorite events and create a personalized conference calendar that works for you.
              </p>
            </div>
          </div>
        </div>

        {/* Organizations We Serve */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">Organizations We Serve</h2>
          <p className="text-sm text-gray-500 dark:text-gray-500 text-center mb-6">
            Conference names, organization names, and trademarks remain the property of their respective owners and are used solely for identification and informational purposes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 mb-3">
                <span className="text-xl font-bold">UPCI</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">United Pentecostal Church International</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">General Conferences, Youth Congress, Bible Quizzing, and more</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 mb-3">
                <span className="text-xl font-bold">WPF</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">World Pentecostal Fellowship</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">International conferences and global gatherings</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 mb-3">
                <span className="text-xl font-bold">ALJC</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Assemblies of the Lord Jesus Christ</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Youth events and denominational gatherings</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 mb-3">
                <span className="text-xl font-bold">AFF</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Apostolic Faith Fellowship</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Family conferences, women's retreats, and ministry events</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-gray-100 dark:bg-gray-800/50 rounded-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mb-3 text-xl font-bold">
                1
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Browse Events</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Explore upcoming conferences by organization, date, or location
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mb-3 text-xl font-bold">
                2
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Get Details</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View schedules, speakers, venue information, and travel options
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mb-3 text-xl font-bold">
                3
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Plan Your Trip</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Book flights, reserve hotels, and organize your conference experience
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mb-3 text-xl font-bold">
                4
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Attend & Enjoy</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Experience powerful worship, teaching, and fellowship
              </p>
            </div>
          </div>
        </div>

        {/* Contact/Submit Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">Get Involved</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 text-center mb-6">
            Have an event you'd like to feature? Want to help improve ACP Conference Planner?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-event"
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors"
            >
              Submit an Event
              <svg className="ml-2 -mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </Link>
            <Link
              to="/events"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Browse Events
              <svg className="ml-2 -mr-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
